import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Crown, Zap, Rocket, Star } from "lucide-react";
import { PlanCard } from "./PlanCard";
import type { SubscriptionStatus } from "@/hooks/use-subscription-plans";

type BillingInterval = "monthly" | "yearly";
type TierCode = "free" | "starter" | "pro" | "expert";

interface Tier {
  code: TierCode;
  name: string;
  displayName: string;
  description: string;
  monthlyPriceCents: number;
  yearlyPriceCents: number;
  creditRateCents: number;
}

interface PlanCardsGridProps {
  tiers: Tier[];
  isLoading: boolean;
  billingInterval: BillingInterval;
  currentTierCode?: TierCode;
  currentBillingPeriod?: BillingInterval; // Add current billing period from subscription
  currentTierPrice: number;
  hasCompany: boolean;
  processingPlanCode: TierCode | null;
  onSelectPlan: (tierCode: TierCode) => void;
  isAdmin: boolean;
  subscriptionStatus?: SubscriptionStatus; // Add subscription status to determine if current plan should be highlighted
  needsSubscription?: boolean; // True if user needs to subscribe (no active sub or canceled/blocked)
  isCanceledButActive?: boolean; // True if subscription is scheduled for cancellation but still active
}

const PLAN_ICONS = {
  free: Star,
  starter: Zap,
  pro: Rocket,
  expert: Crown,
};

export function PlanCardsGrid({
  tiers,
  isLoading,
  billingInterval,
  currentTierCode,
  currentBillingPeriod,
  currentTierPrice,
  hasCompany,
  processingPlanCode,
  onSelectPlan,
  isAdmin,
  subscriptionStatus,
  needsSubscription = false,
  isCanceledButActive = false,
}: PlanCardsGridProps) {
  if (isLoading || tiers.length === 0) {
    return (
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4 max-w-7xl mx-auto" data-testid="plan-cards-grid">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="relative flex flex-col animate-pulse">
            <CardHeader className="pb-8 space-y-4">
              <div className="h-16 bg-muted rounded" />
              <div className="h-8 bg-muted rounded w-3/4" />
              <div className="h-20 bg-muted rounded" />
            </CardHeader>
            <CardContent>
              <div className="h-12 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Filter tiers based on billing interval
  const filteredTiers = tiers.filter(tier => !(billingInterval === 'yearly' && tier.code === 'free'));
  
  // Adjust grid columns: 3 columns for yearly (3 tiers), 4 columns for monthly (4 tiers)
  const gridCols = filteredTiers.length === 3 ? 'md:grid-cols-2 xl:grid-cols-3' : 'md:grid-cols-2 xl:grid-cols-4';

  return (
    <div className={`grid gap-6 ${gridCols} max-w-7xl mx-auto`} data-testid="plan-cards-grid">
      {filteredTiers.map((tier) => {
          const Icon = PLAN_ICONS[tier.code];
          // Only highlight as current if subscription is active AND billing period matches
          const isCurrent = currentTierCode === tier.code && 
            billingInterval === currentBillingPeriod &&
            subscriptionStatus === 'active';
          const isProcessing = processingPlanCode === tier.code;
          const isPopular = tier.code === "pro";
          const isRecommended = tier.code === "pro";
          
          // Allow reactivation if this is the current plan and it's scheduled for cancellation
          const canReactivate = isCurrent && isCanceledButActive;

          return (
            <PlanCard
              key={tier.code}
              tier={tier}
              icon={Icon}
              billingInterval={billingInterval}
              isRecommended={isRecommended}
              isPopular={isPopular}
              isCurrent={isCurrent}
              isProcessing={isProcessing}
              hasCompany={hasCompany}
              currentTierPrice={currentTierPrice}
              currentBillingPeriod={currentBillingPeriod}
              onSelectPlan={() => onSelectPlan(tier.code)}
              isAdmin={isAdmin}
              needsSubscription={needsSubscription}
              canReactivate={canReactivate}
            />
          );
        })}
    </div>
  );
}
